import React from 'react';
import {ItemType} from 'antd/es/menu/hooks/useItems';
import {MenuAction, ActionColumn, CodeInput, CodeColumn, CustomInfoButton} from '../../Dashboard/styles';
import {Dropdown, Menu} from 'antd';
import {ButtonMore, ButtonTypes} from '../../../ui-kit/Button';
import {ModalLvl} from './Table';
import {ViewCodeMap} from '../../../helpers/table';
import {CodeSendStatuses, CodeViewFields, CodeViewStateChange, CodeViewStateT} from '../../../types/codes';
import {ActionBtn} from '../../Settings/styles';
import {PromoStateT} from '../../../hooks/codes';

export type CodeViewInputItemPropsT = {
  state?: CodeViewStateT;
  field: CodeViewFields;
  onChange?: CodeViewStateChange;
  editingId?: string;
  email?: string;
  guestName?: string;
  id?: string;
  codeStatus?: CodeSendStatuses;
};

export const CodeViewInputItem: React.FC<CodeViewInputItemPropsT> = ({
  editingId,
  id,
  email,
  guestName,
  onChange,
  field,
  state,
  codeStatus,
}) => {
  const isCurrentEditing = id && editingId === id;
  if (!id) return null;
  const handleChange = (e: any) => onChange?.({key: field, id, value: e?.target?.value});
  const value = field === CodeViewFields.email ? email : guestName;

  if (codeStatus === CodeSendStatuses.externalShare)
    return (
      <CodeColumn>
        <div>Code marked as shared externally</div>
      </CodeColumn>
    );
  //remove actions
  return <CodeColumn>{value && <div>{value}</div>}</CodeColumn>;

  if (isCurrentEditing)
    return (
      <CodeColumn>
        <CodeInput placeholder={`Type ${field}...`} value={state?.[field] || ''} onChange={handleChange} />
      </CodeColumn>
    );

  return (
    <CodeColumn>
      {value || email ? <div>{value}</div> : <CodeInput placeholder={`Type ${field}...`} onChange={handleChange} />}
    </CodeColumn>
  );
};

export type CodeViewPromoItemPropsT = {code?: string};
export const CodeViewPromoItem: React.FC<CodeViewPromoItemPropsT> = ({code}) => {
  return (
    <CodeColumn>
      <div>{code}</div>
    </CodeColumn>
  );
};

export type CodeViewActionsItemProps = {
  edit?: boolean;
  id?: string;
  codeStatus?: CodeSendStatuses;
  onUpdateCode?: (id: string) => void;
  onCancelEdit?: () => void;
  onSendCode?: (id?: string) => void;
  setFocusId?: (id?: string) => void;
  toggleShare?: (id?: string, unshare?: boolean) => void;
  loading?: boolean;
  setEditingId?: (id?: string) => void;
  editingId?: string;
  setDelPopup?: (lvl: ModalLvl) => void;
  state?: PromoStateT;
  email?: string;
  shareLoading?: string;
};

export const CodeViewActionsItem: React.FC<CodeViewActionsItemProps> = ({
  codeStatus,
  onSendCode,
  id,
  setFocusId,
  loading,
  setEditingId,
  editingId,
  setDelPopup,
  email,
  state,
  onUpdateCode,
  toggleShare,
  shareLoading,
}) => {
  const isCurrentEditing = id && editingId === id;
  const setEdit = () => setEditingId?.(id);
  const onCancelEdit = () => setEditingId?.('');
  const onSend = () => {
    setFocusId?.(id);
    onSendCode?.(id);
  };
  if (!id) return null;
  const canSave = state?.[id]?.valid;
  const onDelete = () => {
    setFocusId?.(id);
    setDelPopup?.(ModalLvl.confirm);
  };

  const onUpdate = () => onUpdateCode?.(id);
  const isAssigned = !!email && codeStatus === CodeSendStatuses.notsent;
  const notSent = !email && codeStatus === CodeSendStatuses.notsent;
  const shared = codeStatus === CodeSendStatuses.externalShare;
  const onShare = () => toggleShare?.(id, false);
  const onUnshare = () => toggleShare?.(id, true);
  const isShareLoading = shareLoading?.includes(`[${id}]`);
  if (notSent)
    return (
      <ActionColumn>
        <MenuMoreCodeView toggleShare={onShare} shareText={'Mark As Shared'} disabled={isShareLoading} />
      </ActionColumn>
    );
  if (shared)
    return (
      <ActionColumn>
        <CustomInfoButton variant={ViewCodeMap?.shared}>Shared</CustomInfoButton>
        <MenuMoreCodeView toggleShare={onUnshare} shareText={'Mark as unshared'} disabled={isShareLoading} />
      </ActionColumn>
    );
  return (
    <ActionColumn>
      {isAssigned && <CustomInfoButton variant={ViewCodeMap?.assigned}>Assigned</CustomInfoButton>}
      {codeStatus === CodeSendStatuses.queued && (
        <CustomInfoButton variant={ViewCodeMap?.sent}>Queued</CustomInfoButton>
      )}
      {codeStatus === CodeSendStatuses.sent && (
        <CustomInfoButton variant={ViewCodeMap?.sent}>Code Sent</CustomInfoButton>
      )}
    </ActionColumn>
  );

  if (!email)
    return (
      <ActionColumn>
        <ActionBtn variant={ButtonTypes.secondary} onClick={onUpdate} disabled={loading || !canSave}>
          Save
        </ActionBtn>
      </ActionColumn>
    );

  if (isCurrentEditing)
    return (
      <ActionColumn>
        <ActionBtn variant={ButtonTypes.secondary} onClick={onCancelEdit} disabled={loading}>
          Cancel
        </ActionBtn>
        <ActionBtn variant={ButtonTypes.secondary} onClick={onUpdate} disabled={loading || !canSave}>
          Update
        </ActionBtn>
      </ActionColumn>
    );

  if (codeStatus === CodeSendStatuses.notsent && email)
    return (
      <ActionColumn>
        <ActionBtn variant={ButtonTypes.secondary} onClick={setEdit} disabled={loading}>
          Edit
        </ActionBtn>
        <ActionBtn variant={ButtonTypes.secondary} onClick={onSend} disabled={loading}>
          Send
        </ActionBtn>
        <ActionBtn variant={ButtonTypes.tertiary} onClick={onDelete} disabled={loading}>
          Delete
        </ActionBtn>
      </ActionColumn>
    );

  return (
    <ActionColumn>
      {codeStatus === CodeSendStatuses.queued && (
        <CustomInfoButton variant={ViewCodeMap?.sent}>Queued</CustomInfoButton>
      )}
      {codeStatus === CodeSendStatuses.sent && (
        <CustomInfoButton variant={ViewCodeMap?.sent}>Code Sent</CustomInfoButton>
      )}
      <>{id && email && <MenuMorePromocode onEdit={setEdit} />}</>
    </ActionColumn>
  );
};

export type MenuMorePromocodeProps = {
  id?: string;
  onEdit?: () => void;
  onSend?: () => void;
};

export const MenuMorePromocode: React.FC<MenuMorePromocodeProps> = ({onEdit}) => {
  const ddActions: ItemType[] = [{key: 1, label: <MenuAction onClick={onEdit}>Edit</MenuAction>}];

  return (
    <Dropdown overlay={<Menu items={ddActions} />} trigger={['click']}>
      <ButtonMore />
    </Dropdown>
  );
};

export type MenuMoreCodeViewProps = {
  toggleShare?: () => void;
  shareText?: string;
  disabled?: boolean;
};

export const MenuMoreCodeView: React.FC<MenuMoreCodeViewProps> = ({toggleShare, shareText, disabled}) => {
  const ddActions: ItemType[] = [{key: 1, label: <MenuAction onClick={toggleShare}>{shareText}</MenuAction>}];

  return (
    <Dropdown overlay={<Menu items={ddActions} />} trigger={['click']} disabled={disabled}>
      <ButtonMore />
    </Dropdown>
  );
};
